import {InvoiceEntity} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime"
import {DefaultText} from "../../text/DefaultText/DefaultText"
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode"
import {TransportCategoryCell} from "../../data-table/cells/TransportCategoryCell/TransportCategoryCell";
import {HaulMethodCell} from "../../data-table/cells/HaulMethodCell/HaulMethodCell";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency"
import {FormattedDateTimeRange} from "../../date/FormattedDateTimeRange/FormattedDateTimeRange";
import {NotApplicableText} from "../../text/NotApplicableText/NotApplicableText"
import {EntityAnchor} from "@ots-frontend/ots-ui";

export type InvoiceDetailsProps = {
  invoice: InvoiceEntity
}

export const InvoiceDetails = (props: InvoiceDetailsProps) => {
  const {invoice} = props;

  const {t} = useTranslation(["translation", "common"]);

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.invoice.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.invoicenumber.label", "Invoice Number")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.invoiceNumber}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.parentinvoicenumber.label", "Parent Invoice Number")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.parentInvoiceNumber}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.date.label", "Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={invoice.createDttm} displayTime={false}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.serviceperiod.label", "Service Period")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTimeRange
                  start={invoice.fromDttm}
                  end={invoice.toDttm}
                  displayTime={false}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.status.label", "Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={invoice.status}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.operator.label", "Operator")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={invoice.operatorName}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.vendor.label", "Vendor")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={invoice.vendorName}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.secondaryvendor.label", "Secondary Vendor")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.secondaryVendorId && <DefaultText value={invoice.secondaryVendorName}/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.category.label", "Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <TransportCategoryCell category={invoice.category}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.method.label", "Method")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <HaulMethodCell haulMethod={invoice.method}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.site.label", "Site")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.siteId ? <DefaultText value={invoice.siteName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.container.label", "Container")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.containerId ? <DefaultText value={invoice.containerName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.well.label", "Well")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.wellId ? <DefaultText value={invoice.wellName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.rig.label", "Rig")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.rigId ? <DefaultText value={invoice.rigName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.route.label", "Route")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.routeId ? <DefaultText value={invoice.routeName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.operatingarea.label", "Operating Area")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.operatingAreaId ? <EntityAnchor value={{
                  objectId: invoice.operatorId,
                  objectName: invoice.operatingAreaName,
                  objectType: "OPERATINGAREA",
                }}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.freightTypeId ? <DefaultText value={invoice.freightTypeName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.freighttypecategory.label", "Freight Type Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.freightTypeCategory ?? <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.servicetype.label", "Service Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.serviceTypeId ? <DefaultText value={invoice.serviceTypeName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.servicetypecategory.label", "Service Type Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.serviceTypeCategory ?? <NotApplicableText/>}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.invoice.info.field.authorizedby.label", "Authorized By")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {invoice.authorizedById ? <DefaultText value={invoice.authorizedByName}/> : <NotApplicableText/>}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Grid>
          <Grid.Col span={12}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>

              <Group position="apart" pb="sm">
                <Title order={4}>{t("profile.invoice.info.section.financials.label", "Financials")}</Title>
              </Group>

              <Divider/>

              <Grid>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={700} mt="md">
                    {t("profile.invoice.info.field.totalcost.label", "Total Cost")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <FormattedCurrency value={invoice.totalCost}/>
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.invoice.info.field.cost.label", "Cost")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <FormattedCurrency value={invoice.cost}/>
                  </Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.invoice.info.field.taxrate.label", "Tax Rate")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <FormattedCurrency value={invoice.taxRate}/>
                  </Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.invoice.info.field.taxcost.label", "Tax Cost")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <FormattedCurrency value={invoice.taxCost}/>
                  </Text>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.invoice.info.field.lineitemcount.label", "Line Items")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    {invoice.tlis?.length ?? 0}
                  </Text>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
          <Grid.Col span={12}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>

              <Group position="apart" pb="sm">
                <Title order={4}>{t("profile.invoice.info.section.coding.label", "Coding")}</Title>
              </Group>

              <Divider mb="md"/>

              {invoice.codes &&
                <>
                  {invoice.codes.map((code) => {
                    return <DefaultText value={code.code}/>
                  })}
                </>
              }
            </Card>
          </Grid.Col>
        </Grid>
      </Grid.Col>

      {/*<Grid.Col span={12}>*/}
      {/*  <Card shadow="sm" padding="lg" radius="md" withBorder>*/}

      {/*    <Group position="apart" pb="sm">*/}
      {/*      <Title order={4}>Line Items</Title>*/}
      {/*    </Group>*/}

      {/*    <Divider pb="sm"/>*/}

      {/*    {invoice.tlis &&*/}
      {/*      <>*/}
      {/*        <ol>*/}
      {/*          {invoice.tlis.map((tli) => {*/}
      {/*            return (*/}
      {/*              <li>{tli.resourceId}</li>*/}
      {/*            )*/}
      {/*          })}*/}
      {/*        </ol>*/}
      {/*      </>*/}
      {/*    }*/}
      {/*  </Card>*/}
      {/*</Grid.Col>*/}
    </Grid>
  );
};
